import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, defineScreen } from '@/shared/screen/define';
import { dynamicRulesFilters } from './dynamic-rules.filters';

const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela da regra dinâmica para executar esta ação.`);
};

export const DYNAMIC_RULES_PATH = '/cadastro/regras-dinamicas';
export const NEW_DYNAMIC_RULE_ID = 'novo';
export const dynamicRulePath = (id: string) => `${DYNAMIC_RULES_PATH}/${encodeURIComponent(id)}`;

export const dynamicRulesCreate = action({
  id: 'dynamicRules.create',
  kind: 'draft',
  label: msg`Nova Regra`,
  description: msg`Abre o formulário em branco para criar uma regra dinâmica de crédito.`,
  run: ({ nav }) => nav.to(dynamicRulePath(NEW_DYNAMIC_RULE_ID)),
});

export const dynamicRulesOpen = action({
  id: 'dynamicRules.open',
  kind: 'navigate',
  label: msg`Abrir`,
  description: msg`Abre uma regra dinâmica pelo id, com as condições, a ação e as mensagens.`,
  row: true,
  page: true,
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(dynamicRulePath(input.id)),
});

export const dynamicRulesListRefresh = action({
  id: 'dynamicRules.list.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca a lista de regras dinâmicas de novo, sem mudar nada.`,
  run: mountedScreenRequired,
});

export const dynamicRulesFilter = filterAction('dynamicRules.filter', dynamicRulesFilters, {
  description: msg`Filtra a lista de regras dinâmicas: searchString (busca livre) e name (nome). Filtro omitido é limpo.`,
});

export const dynamicRulesListPage = listPageAction('dynamicRules.list.page');

export const dynamicRulesListScreen = defineScreen({
  id: 'dynamicRules.list',
  path: DYNAMIC_RULES_PATH,
  title: msg`Regras Dinâmicas`,
  description: msg`Lista de regras dinâmicas de crédito, com a quantidade de condições, quem criou, a data de criação, busca, paginação, abrir e criar.`,
  place: { section: 'register', icon: 'sparkles' },
  actions: [dynamicRulesCreate, dynamicRulesListRefresh, dynamicRulesFilter, dynamicRulesListPage, dynamicRulesOpen],
});

export const dynamicRuleBack = action({
  id: 'dynamicRules.back',
  kind: 'navigate',
  label: msg`Regras Dinâmicas`,
  description: msg`Volta para a lista de regras dinâmicas.`,
  run: ({ nav }) => nav.to(DYNAMIC_RULES_PATH),
});

export const dynamicRuleHistory = action({
  id: 'dynamicRules.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o painel com o histórico de alterações da regra dinâmica aberta.`,
  run: mountedScreenRequired,
});

export const dynamicRuleSave = action({
  id: 'dynamicRules.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Confere e salva a regra dinâmica aberta, com o nome, as condições, a ação e as mensagens que estão no formulário. A regra passa a valer nas próximas análises de crédito. Pede a confirmação da pessoa.`,
  preview: () => ({
    title: t`Salvar regra dinâmica`,
    detail: t`A regra passa a valer nas próximas análises de crédito.`,
  }),
  run: mountedScreenRequired,
});

export const dynamicRuleDetailScreen = defineScreen({
  id: 'dynamicRules.detail',
  path: `${DYNAMIC_RULES_PATH}/$ruleId`,
  title: msg`Regra Dinâmica`,
  description: msg`Cadastro de uma regra dinâmica (nova, edição ou cópia): identificação, condições, fórmula, ação e mensagens; salvar e histórico.`,
  place: { parent: 'dynamicRules.list', icon: 'file-text', record: msg`Regra Dinâmica` },
  actions: [dynamicRuleBack, dynamicRuleHistory, dynamicRuleSave],
});

export const dynamicRulesSimulateRun = action({
  id: 'dynamicRules.simulate.run',
  kind: 'read',
  label: msg`Simular`,
  description: msg`Roda as regras dinâmicas com os dados de entrada preenchidos na tela e mostra o resultado, sem gravar nada.`,
  run: mountedScreenRequired,
});

export const dynamicRulesSimulateScreen = defineScreen({
  id: 'dynamicRules.simulate',
  path: `${DYNAMIC_RULES_PATH}/simular`,
  title: msg`Simular Regra Dinâmica`,
  description: msg`Simulação das regras dinâmicas com dados de trabalhador, empregador e operação, sem gravar nada.`,
  place: { parent: 'dynamicRules.list', icon: 'receipt-text' },
  actions: [dynamicRuleBack, dynamicRulesSimulateRun],
});
